import React from 'react';
import { useVisit } from '../../VisitContext';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getInterventionsToPerformOptions, submitVisitOptions, type PlannedIntervention } from '@generated';
import { InterventionTable } from '~/features/careplanning/goals-interventions/interventions/InterventionTable';
import { Form } from '~/shared/forms/Form';
import Yup from '~/util/yup';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { DateTimeFormField } from '~/shared/forms/controls/fields/date-fields/DateTimeFormField';
import { FooterButtonContainer } from '~/shared/forms/buttons/FooterButtonContainer';
import { FormSubmitButton } from '~/shared/forms/buttons/FormSubmitButton';
import { NextVisit } from './NextVisit';
import { useGetVisitOptions, useGetVisitStatusOptions } from '../../queries';

const validationSchema = Yup.object({
	travelTimeIn: Yup.date().label('Travel Time In').nullable().max(Yup.ref('timeIn'), 'Travel time in must come before time in.'),
	travelTimeOut: Yup.date().label('Travel Time Out').nullable().min(Yup.ref('timeOut'), 'Travel time out must come after time out.'),
	associatedMileage: Yup.number().label('Associated Mileage').typeError('Must be a positive, number only').min(0, 'Must be greater than 0'),
	documentationTime: Yup.number()
		.label('Documentation Time')
		.typeError('Must be a positive, number only')
		.min(0, 'Must be greater than 0')
		.nullable(),
	plannedInterventionLength: Yup.number().min(1, 'At least one planned intervention must be selected')
});

export function SubmitVisit() {
	const { id, stayId, performingUserId, travelTimeInUtc, travelTimeOutUtc, associatedMileage, documentationTime } = useVisit();
	const [plannedInterventions, setPlannedInterventions] = React.useState<PlannedIntervention[]>([]);
	const { data: interventionsToPerform } = useSuspenseQuery(getInterventionsToPerformOptions(stayId, performingUserId));
	const { refetch: refetchVisitStatus } = useQuery(useGetVisitStatusOptions(id));
	const { refetch: refetchVisit } = useQuery(useGetVisitOptions(id));

	const plannedInterventionIds = plannedInterventions?.map(i => i.interventionId) ?? [];

	const onPlannedInterventionsChange = (ids: string[]) => {
		const selectedInterventions = interventionsToPerform?.filter(i => ids.includes(i.id)) ?? [];

		setPlannedInterventions(prev => [
			...selectedInterventions.map(x => ({ interventionId: x.id, notes: prev.find(y => y.interventionId === x.id)?.notes ?? '' }))
		]);
	};

	return (
		<Form
			initialValues={{
				visitId: id,
				travelTimeIn: travelTimeInUtc ?? null,
				travelTimeOut: travelTimeOutUtc ?? null,
				associatedMileage: associatedMileage,
				documentationTime: documentationTime,
				plannedInterventionLength: 0
			}}
			saveMutation={submitVisitOptions}
			validationSchema={validationSchema}
			onSaveTransform={values => {
				return {
					...values,
					plannedInterventions
				};
			}}
			onSuccess={() => {
				refetchVisit();
				refetchVisitStatus();
			}}
		>
			{formHelpers => {
				const onInterventionsChange = (ids: string[]) => {
					onPlannedInterventionsChange(ids);
					formHelpers.setFieldValue('plannedInterventionLength', ids.length);
					formHelpers.validateField('plannedInterventionLength');
				};
				return (
					<div className="space-y-8">
						<div className="space-y-4">
							<div className="flex items-center justify-between gap-3">
								<p className="font-semibold">Plan for next visit:</p>
								<NextVisit />
							</div>
							<InterventionTable
								rows={interventionsToPerform ?? []}
								selection={{
									selectedIds: plannedInterventionIds,
									onSelectionChange: ids => onInterventionsChange(ids)
								}}
							/>
						</div>
						<div className="grid grid-cols-4 gap-6">
							<TextFormField name="documentationTime" />
							<TextFormField name="associatedMileage" />
							<DateTimeFormField name="travelTimeIn" granularity="minute" />
							<DateTimeFormField name="travelTimeOut" granularity="minute" />
						</div>
						<FooterButtonContainer className="border-t border-gray-200 pt-6">
							<FormSubmitButton />
						</FooterButtonContainer>
					</div>
				);
			}}
		</Form>
	);
}
